import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactDOM from 'react-dom';
import {
    LineChart, Line, AreaChart, Area, BarChart, Bar,
    PieChart, Pie, Cell, ResponsiveContainer,
    XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip
} from 'recharts';
import logo from '../assets/sclogo-250-1.svg';
import PricingIntelligenceTable from './PricingIntelligenceTable';
import Tooltip from './Tooltip';

/**
 * Searchable Vendor Dropdown Component
 */
const VendorSearchableDropdown = ({ vendors, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredVendors, setFilteredVendors] = useState(vendors);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredVendors(vendors);
        } else {
            const filtered = vendors.filter(vendor => 
                vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                vendor.id.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredVendors(filtered);
        }
    }, [searchTerm, vendors]);

    const selectedVendor = vendors.find(vendor => vendor.id === value);
    const displayText = value === 'all' ? 'All Vendors' : 
        selectedVendor ? selectedVendor.name : 'All Vendors';

    const handleSelect = (vendorId) => {
        onChange(vendorId);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            {/* Dropdown Button */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    width: '100%',
                    padding: '8px 32px 8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            >
                <span style={{ 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    color: value === 'all' ? '#6b7280' : '#111827'
                }}>
                    {displayText}
                </span>
                <svg 
                    style={{ 
                        width: '16px', 
                        height: '16px', 
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s'
                    }}
                    fill="none" 
                    stroke="#6b7280" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    viewBox="0 0 24 24"
                >
                    <path d="M19 9l-7 7-7-7"/>
                </svg>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    zIndex: 50,
                    maxHeight: '300px',
                    overflow: 'hidden'
                }}>
                    {/* Search Input */}
                    <div style={{ padding: '8px' }}>
                        <input
                            type="text"
                            placeholder="Search vendors..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '6px 8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '14px',
                                outline: 'none'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                            autoFocus
                        />
                    </div>

                    {/* Options List */}
                    <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                        {/* All Vendors Option */}
                        <button
                            type="button"
                            onClick={() => handleSelect('all')}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                textAlign: 'left',
                                backgroundColor: value === 'all' ? '#f3f4f6' : 'white',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: '#374151'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = value === 'all' ? '#f3f4f6' : 'white'}
                        >
                            All Vendors
                        </button>

                        {/* Vendor Options */}
                        {filteredVendors.map((vendor) => (
                            <button
                                key={vendor.id}
                                type="button"
                                onClick={() => handleSelect(vendor.id)}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    textAlign: 'left',
                                    backgroundColor: value === vendor.id ? '#f3f4f6' : 'white',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: '#374151',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = value === vendor.id ? '#f3f4f6' : 'white'}
                            >
                                {vendor.name}
                            </button>
                        ))}

                        {/* No Results */}
                        {filteredVendors.length === 0 && (
                            <div style={{
                                padding: '12px',
                                textAlign: 'center',
                                color: '#6b7280',
                                fontSize: '14px'
                            }}>
                                No vendors found
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Click Outside Handler */}
            {isOpen && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 40
                    }}
                    onClick={() => setIsOpen(false)}
                />
            )}
        </div>
    );
};

/**
 * Searchable GDC Dropdown Component
 */
const GDCSearchableDropdown = ({ gdcs, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredGdcs, setFilteredGdcs] = useState(gdcs);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredGdcs(gdcs);
        } else {
            const filtered = gdcs.filter(gdc => 
                gdc.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                gdc.description.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredGdcs(filtered);
        }
    }, [searchTerm, gdcs]);

    const selectedGdc = gdcs.find(gdc => gdc.code === value);
    const displayText = value === 'all' ? 'All GDCs' : 
        selectedGdc ? `${selectedGdc.code} - ${selectedGdc.description}` : 'All GDCs';

    const handleSelect = (gdcCode) => {
        onChange(gdcCode);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            {/* Dropdown Button */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    width: '100%',
                    padding: '8px 32px 8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            >
                <span style={{ 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    color: value === 'all' ? '#6b7280' : '#111827'
                }}>
                    {displayText}
                </span>
                <svg 
                    style={{ 
                        width: '16px', 
                        height: '16px', 
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s'
                    }}
                    fill="none" 
                    stroke="#6b7280" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    viewBox="0 0 24 24"
                >
                    <path d="M19 9l-7 7-7-7"/>
                </svg>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    zIndex: 50,
                    maxHeight: '300px',
                    overflow: 'hidden'
                }}>
                    {/* Search Input */}
                    <div style={{ padding: '8px' }}>
                        <input
                            type="text"
                            placeholder="Search GDCs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '6px 8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '14px',
                                outline: 'none'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                            autoFocus
                        />
                    </div>

                    {/* Options List */}
                    <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                        {/* All GDCs Option */}
                        <button
                            type="button"
                            onClick={() => handleSelect('all')}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                textAlign: 'left',
                                backgroundColor: value === 'all' ? '#f3f4f6' : 'white',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: '#374151'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = value === 'all' ? '#f3f4f6' : 'white'}
                        >
                            All GDCs
                        </button>

                        {/* GDC Options */}
                        {filteredGdcs.map((gdc) => (
                            <button
                                key={gdc.code}
                                type="button"
                                onClick={() => handleSelect(gdc.code)}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    textAlign: 'left',
                                    backgroundColor: value === gdc.code ? '#f3f4f6' : 'white',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: '#374151',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = value === gdc.code ? '#f3f4f6' : 'white'}
                            >
                                <strong>{gdc.code}</strong> - {gdc.description}
                            </button>
                        ))}

                        {/* No Results */}
                        {filteredGdcs.length === 0 && (
                            <div style={{
                                padding: '12px',
                                textAlign: 'center',
                                color: '#6b7280',
                                fontSize: '14px'
                            }}>
                                No GDCs found
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Click Outside Handler */}
            {isOpen && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 40
                    }}
                    onClick={() => setIsOpen(false)}
                />
            )}
        </div>
    );
};

/**
 * Modern Minimalist Dashboard with Clean Filters
 * 
 * @author SureCost Development Team
 * @version 6.0.0
 */
const Dashboard = () => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [vendors, setVendors] = useState([]);
    const [months, setMonths] = useState([]);
    const [cohorts, setCohorts] = useState([]);
    const [gdcs, setGdcs] = useState([]);
    const [kpiData, setKpiData] = useState({
        aboveMarket: 0,
        aboveLeader: 0,
        atOrBelowMarket: 0,
        atOrBelowLeader: 0,
        aboveMarketPct: 0,
        aboveLeaderPct: 0,
        atOrBelowMarketPct: 0,
        itemsInScope: 0
    });
    
    // Band distribution data
    const [bandDistribution, setBandDistribution] = useState([]);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [exportingFormat, setExportingFormat] = useState(null);
    
    // Clean filter state with auto-apply
    const [filters, setFilters] = useState({
        dateRange: '1m',
        month: '202505', // Default to May 2025 where we have data
        cohort: 'all',
        gdc: 'all',
        vendor: 'all'
    });
    
    
    const navigate = useNavigate();

    useEffect(() => {
        verifyAuthenticationAndLoadUser();
    }, [navigate]);

    // Load vendors for admin users
    useEffect(() => {
        if (user && user.tenant && user.tenant.isAdmin) {
            loadVendors();
        }
    }, [user]);

    // Load metadata (months, cohorts, GDCs) when user is loaded, but delay data loading
    useEffect(() => {
        if (user) {
            loadMonths();
            loadCohorts();
            loadGdcs();
            
            // Delay initial data loading to allow tenant vendor filter to be applied
            setTimeout(() => {
                loadKpiData();
                loadBandDistribution();
            }, 200);
        }
    }, [user]);

    // Auto-apply filters on change and reload KPI data and band distribution
    useEffect(() => {
        applyFilters();
        if (user && !isLoading) {
            loadKpiData();
            loadBandDistribution();
        }
    }, [filters]);

    // Handle export functionality
    const handleExport = async (format) => {
        if (isExporting) return; // Prevent multiple clicks
        
        setShowExportMenu(false);
        setIsExporting(true);
        setExportingFormat(format);
        
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                console.error('No authentication token found');
                return;
            }

            // Prepare filter parameters
            const params = new URLSearchParams();
            params.append('format', format);
            params.append('vendor_id', filters.vendor === 'all' ? '' : filters.vendor);
            params.append('month_key', filters.month);
            params.append('date_range', filters.dateRange);
            params.append('cohort_id', filters.cohort === 'all' ? '' : filters.cohort);
            params.append('gdc_code', filters.gdc === 'all' ? '' : filters.gdc);
            
            const response = await fetch(`/api/export?${params.toString()}`);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Export failed:', errorText);
                throw new Error(`Export failed: ${response.status}`);
            }

            // Get the blob from response
            const blob = await response.blob();
            console.log('Export blob size:', blob.size, 'bytes');
            
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            
            // Set filename based on format and vendor
            const vendorName = filters.vendor === 'all' ? 'All_Vendors' : 
                vendors.find(v => v.id === filters.vendor)?.name?.replace(/\s+/g, '_') || filters.vendor;
            const timestamp = new Date().toISOString().slice(0, 10);
            a.download = `${vendorName}_Pricing_Dashboard_${timestamp}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
            
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            
        } catch (error) {
            console.error(`Error exporting ${format}:`, error);
            alert('Export failed. Please try again.');
        } finally {
            setIsExporting(false);
            setExportingFormat(null);
        }
    };

    // Close export menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showExportMenu && !event.target.closest('[data-export-menu]')) {
                setShowExportMenu(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showExportMenu]);

    const verifyAuthenticationAndLoadUser = async () => {
        try {
            const authToken = localStorage.getItem('authToken');
            const storedUserData = localStorage.getItem('userData');
            
            if (!authToken || !storedUserData) {
                navigate('/');
                return;
            }
            
            // Validate session with backend
            try {
                const validateResponse = await fetch('/api/auth/validate', {
                    headers: {
                        'Authorization': `Bearer ${authToken}`
                    }
                });
                
                const validateData = await validateResponse.json();
                
                if (!validateResponse.ok || !validateData.valid) {
                    console.log('⚠️ Session expired or invalid, redirecting to login');
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('userData');
                    navigate('/', { state: { message: 'Your session has expired. Please sign in again.' } });
                    return;
                }
                
                console.log(`🔐 Session valid, expires in ${validateData.session?.hoursRemaining} hours`);
            } catch (validateError) {
                console.error('Failed to validate session:', validateError);
                // Continue with cached data if validation fails (offline mode)
            }
            
            // Use the actual user data from authentication
            const userData = JSON.parse(storedUserData);
            console.log('👤 User data loaded:', userData);
            console.log('🏢 Current tenant:', userData.tenant);
            console.log('🎯 Tenant default vendor:', userData.tenant?.defaultVendorId);
            
            // Enrich user data with additional fields for display
            userData.name = userData.email.split('@')[0].replace(/[._-]/g, ' ')
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
            userData.company = userData.tenant.name;
            userData.role = userData.tenant.isAdmin ? 'Administrator' : 'User';
            
            // Load tenant details to get default vendor (only for admins)
            if (userData.tenant.isAdmin) {
                try {
                    const tenantsResponse = await fetch('/api/admin/tenants', {
                        headers: {
                            'Authorization': `Bearer ${authToken}`
                        }
                    });
                    
                    if (tenantsResponse.ok) {
                        const tenantsData = await tenantsResponse.json();
                        const currentTenant = tenantsData.tenants.find(t => t.id === userData.tenant.id);
                        
                        if (currentTenant && currentTenant.defaultVendorId) {
                            userData.tenant.defaultVendorId = currentTenant.defaultVendorId;
                            
                            // Validate that the default vendor exists in Snowflake before applying
                            try {
                                const vendorsResponse = await fetch('/api/snowflake/vendors');
                                const vendorsData = await vendorsResponse.json();
                                
                                if (vendorsData.success && vendorsData.vendors) {
                                    console.log(`🔍 Checking if vendor "${currentTenant.defaultVendorId}" exists in Snowflake...`);
                                    console.log('📋 Available vendors:', vendorsData.vendors.map(v => `${v.id} (${v.name})`));
                                    
                                    const vendorExists = vendorsData.vendors.some(v => 
                                        v.id === currentTenant.defaultVendorId || 
                                        v.name === currentTenant.defaultVendorId
                                    );
                                    
                                    if (vendorExists) {
                                        // Set the default vendor filter
                                        setFilters(prev => ({
                                            ...prev,
                                            vendor: currentTenant.defaultVendorId
                                        }));
                                        
                                        console.log(`✅ Applied default vendor filter: ${currentTenant.defaultVendorId}`);
                                    } else {
                                        console.warn(`⚠️ Default vendor "${currentTenant.defaultVendorId}" not found in Snowflake data. Using "all" instead.`);
                                        setFilters(prev => ({
                                            ...prev,
                                            vendor: 'all'
                                        }));
                                    }
                                } else {
                                    console.warn('⚠️ Could not load vendors from Snowflake. Using "all" vendor filter.');
                                    setFilters(prev => ({
                                        ...prev,
                                        vendor: 'all'
                                    }));
                                }
                            } catch (vendorError) {
                                console.warn('⚠️ Error validating default vendor:', vendorError);
                                setFilters(prev => ({
                                    ...prev,
                                    vendor: 'all'
                                }));
                            }
                        }
                    }
                } catch (error) {
                    console.log('Could not load tenant default vendor:', error);
                }
            } else {
                // For non-admin tenants, load their specific vendor dashboard settings
                try {
                    const tenantsResponse = await fetch('/api/admin/tenants', {
                        headers: {
                            'Authorization': `Bearer ${authToken}`
                        }
                    });
                    
                    if (tenantsResponse.ok) {
                        const tenantsData = await tenantsResponse.json();
                        const currentTenant = tenantsData.tenants.find(t => t.id === userData.tenant.id);
                        
                        if (currentTenant && currentTenant.defaultVendorId) {
                            // Validate that the default vendor exists in Snowflake before applying
                            try {
                                const vendorsResponse = await fetch('/api/snowflake/vendors');
                                const vendorsData = await vendorsResponse.json();
                                
                                if (vendorsData.success && vendorsData.vendors) {
                                    console.log(`🔍 Checking if vendor "${currentTenant.defaultVendorId}" exists in Snowflake...`);
                                    console.log('📋 Available vendors:', vendorsData.vendors.map(v => `${v.id} (${v.name})`));
                                    
                                    const vendorExists = vendorsData.vendors.some(v => 
                                        v.id === currentTenant.defaultVendorId || 
                                        v.name === currentTenant.defaultVendorId
                                    );
                                    
                                    if (vendorExists) {
                                        // Non-admin tenants should only see their assigned vendor
                                        setFilters(prev => ({
                                            ...prev,
                                            vendor: currentTenant.defaultVendorId
                                        }));
                                        
                                        console.log(`✅ Applied vendor filter for tenant: ${currentTenant.defaultVendorId}`);
                                    } else {
                                        console.warn(`⚠️ Tenant vendor "${currentTenant.defaultVendorId}" not found in Snowflake data. Using "all" instead.`);
                                        setFilters(prev => ({
                                            ...prev,
                                            vendor: 'all'
                                        }));
                                    }
                                } else {
                                    console.warn('⚠️ Could not load vendors from Snowflake. Using "all" vendor filter.');
                                    setFilters(prev => ({
                                        ...prev,
                                        vendor: 'all'
                                    }));
                                }
                            } catch (vendorError) {
                                console.warn('⚠️ Error validating tenant vendor:', vendorError);
                                setFilters(prev => ({
                                    ...prev,
                                    vendor: 'all'
                                }));
                            }
                        }
                    }
                } catch (error) {
                    console.log('Could not load tenant vendor settings:', error);
                }
            }
            
            setUser(userData);
            setIsLoading(false);
        } catch (error) {
            console.error('Authentication failed:', error);
            navigate('/');
        }
    };

    const handleLogout = async () => {
        try {
            const authToken = localStorage.getItem('authToken');
            if (authToken) {
                // Invalidate session on backend
                await fetch('/api/auth/logout', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${authToken}`
                    }
                });
            }
        } catch (error) {
            console.error('Logout error:', error);
        }
        
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        navigate('/');
    };

    const loadVendors = async () => {
        try {
            const authToken = localStorage.getItem('authToken');
            const response = await fetch('/api/snowflake/vendors');
            const data = await response.json();
            if (data.success) {
                setVendors(data.vendors);
                console.log(`✅ Loaded ${data.vendors.length} vendors from Snowflake`);
            } else {
                console.error('Failed to load vendors:', data.message);
                // Fallback to empty array if Snowflake is not connected
                setVendors([]);
            }
        } catch (error) {
            console.error('Failed to load vendors:', error);
            setVendors([]);
        }
    };

    const loadKpiData = async () => {
        try {
            const authToken = localStorage.getItem('authToken');
            
            // Build query parameters from current filters
            const params = new URLSearchParams();
            
            // Map frontend filter values to backend parameters
            if (filters.vendor && filters.vendor !== 'all') {
                params.append('vendor_id', filters.vendor);
            }
            if (filters.gdc && filters.gdc !== 'all') {
                params.append('gdc', filters.gdc);
            }
            if (filters.cohort && filters.cohort !== 'all') {
                // Use cohort ID directly from the API data
                params.append('cohort_id', filters.cohort);
            }
            
            // Handle date filters
            if (filters.month && filters.month !== 'current') {
                params.append('month_key', filters.month);
            }
            
            // Handle date range filters and add months_back parameter
            let monthsBack = 1; // Default to 1 month
            if (filters.dateRange && filters.dateRange !== '1m') {
                switch (filters.dateRange) {
                    case '3m':
                        monthsBack = 3;
                        break;
                    case '6m':
                        monthsBack = 6;
                        break;
                    case '12m':
                        monthsBack = 12;
                        break;
                    default:
                        monthsBack = 1;
                }
            }
            params.append('months_back', monthsBack);
            
            const url = `/api/snowflake/kpis${params.toString() ? '?' + params.toString() : ''}`;
            console.log('🔍 Loading KPI data from:', url);
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });
            const data = await response.json();
            
            if (data.success) {
                setKpiData(data.kpis);
                console.log('✅ Loaded KPI data from Snowflake:', data.kpis);
            } else {
                console.error('Failed to load KPI data:', data.message);
            }
        } catch (error) {
            console.error('Failed to load KPI data:', error);
        }
    };

    const loadMonths = async () => {
        try {
            const authToken = localStorage.getItem('authToken');
            const response = await fetch('/api/snowflake/months', {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });
            const data = await response.json();
            if (data.success) {
                setMonths(data.months);
                console.log(`✅ Loaded ${data.months.length} months from Snowflake`);
            } else {
                console.error('Failed to load months:', data.message);
                // Fallback to empty array if Snowflake is not connected
                setMonths([]);
            }
        } catch (error) {
            console.error('Failed to load months:', error);
            setMonths([]);
        }
    };

    const loadCohorts = async () => {
        try {
            const authToken = localStorage.getItem('authToken');
            const response = await fetch('/api/snowflake/cohorts', {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });
            const data = await response.json();
            if (data.success) {
                setCohorts(data.cohorts);
                console.log(`✅ Loaded ${data.cohorts.length} cohorts from Snowflake`);
            } else {
                console.error('Failed to load cohorts:', data.message);
                // Fallback to empty array if Snowflake is not connected
                setCohorts([]);
            }
        } catch (error) {
            console.error('Failed to load cohorts:', error);
            setCohorts([]);
        }
    };

    const loadGdcs = async () => {
        try {
            const authToken = localStorage.getItem('authToken');
            const response = await fetch('/api/snowflake/gdcs', {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });
            const data = await response.json();
            if (data.success) {
                setGdcs(data.gdcs);
                console.log(`✅ Loaded ${data.gdcs.length} GDCs from Snowflake`);
            } else {
                console.error('Failed to load GDCs:', data.message);
                // Fallback to empty array if Snowflake is not connected
                setGdcs([]);
            }
        } catch (error) {
            console.error('Failed to load GDCs:', error);
            setGdcs([]);
        }
    };

    const loadBandDistribution = async () => {
        try {
            const authToken = localStorage.getItem('authToken');
            
            // Build query params based on filters
            const params = new URLSearchParams();
            
            // Map vendor filter
            if (filters.vendor && filters.vendor !== 'all') {
                params.append('vendor_id', filters.vendor);
            }
            
            // Map month filter
            if (filters.month && filters.month !== 'current') {
                params.append('month_key', filters.month);
            }
            
            // Map date range to months_back
            let monthsBack = 1;
            switch (filters.dateRange) {
                case '1m': monthsBack = 1; break;
                case '3m': monthsBack = 3; break;
                case '6m': monthsBack = 6; break;
                case '12m': monthsBack = 12; break;
                default: monthsBack = 1;
            }
            params.append('months_back', monthsBack);
            
            // Map cohort filter
            if (filters.cohort && filters.cohort !== 'all') {
                params.append('cohort_id', filters.cohort);
                console.log(`🎯 Adding cohort filter: ${filters.cohort}`);
            } else {
                console.log(`🎯 No cohort filter applied (cohort: ${filters.cohort})`);
            }
            
            const url = `/api/snowflake/band-distribution?${params}`;
            console.log(`📊 Loading band distribution from: ${url}`);
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });
            
            const data = await response.json();
            if (data.success && data.data) {
                // Map the API response to the display format
                const formattedData = data.data
                    .filter(item => item.bucket !== null && item.bucket !== undefined) // Filter out null buckets
                    .map(item => {
                        // Define colors based on bucket name
                        let color, bgColor;
                        const bucketName = item.bucket.toLowerCase();
                        
                        if (bucketName.includes('below')) {
                            color = bucketName.includes('≥20%') || bucketName.includes('20%') ? '#059669' : 
                                    bucketName.includes('10') ? '#10b981' : '#34d399';
                            bgColor = '#f0fdf4';
                        } else if (bucketName.includes('above')) {
                            color = bucketName.includes('≥20%') || bucketName.includes('20%') ? '#991b1b' : 
                                    bucketName.includes('10') ? '#dc2626' : '#f87171';
                            bgColor = '#fef2f2';
                        } else {
                            color = '#6b7280';
                            bgColor = '#f9fafb';
                        }
                        
                        return {
                            label: item.bucket,
                            value: item.count,
                            color: color,
                            bgColor: bgColor
                        };
                    })
                    .sort((a, b) => {
                        // Sort buckets in the correct order
                        const order = ['≥20% below', '10–20% below', '5–10% below', '±5% of market', '5–10% above', '10–20% above', '≥20% above'];
                        return order.indexOf(a.label) - order.indexOf(b.label);
                    });
                
                setBandDistribution(formattedData);
                console.log(`✅ Loaded band distribution data:`, formattedData);
            } else {
                console.log('Using default band distribution data');
                // Set default data if API fails
                setBandDistribution([
                    { label: '≥20% below', value: 4, color: '#059669', bgColor: '#f0fdf4' },
                    { label: '10-20% below', value: 3, color: '#10b981', bgColor: '#f0fdf4' },
                    { label: '5-10% below', value: 8, color: '#34d399', bgColor: '#f0fdf4' },
                    { label: '±5% of market', value: 13, color: '#6b7280', bgColor: '#f9fafb' },
                    { label: '5-10% above', value: 6, color: '#f87171', bgColor: '#fef2f2' },
                    { label: '10-20% above', value: 8, color: '#dc2626', bgColor: '#fef2f2' },
                    { label: '≥20% above', value: 9, color: '#991b1b', bgColor: '#fef2f2' }
                ]);
            }
        } catch (error) {
            console.error('Failed to load band distribution:', error);
            // Set default data on error
            setBandDistribution([
                { label: '≥20% below', value: 4, color: '#059669', bgColor: '#f0fdf4' },
                { label: '10-20% below', value: 3, color: '#10b981', bgColor: '#f0fdf4' },
                { label: '5-10% below', value: 8, color: '#34d399', bgColor: '#f0fdf4' },
                { label: '±5% of market', value: 13, color: '#6b7280', bgColor: '#f9fafb' },
                { label: '5-10% above', value: 6, color: '#f87171', bgColor: '#fef2f2' },
                { label: '10-20% above', value: 8, color: '#dc2626', bgColor: '#fef2f2' },
                { label: '≥20% above', value: 9, color: '#991b1b', bgColor: '#fef2f2' }
            ]);
        }
    };

    const applyFilters = () => {
        // Auto-apply logic - would normally trigger API call
        console.log('Filters applied:', filters);
    };

    const updateFilter = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    // Data for visualizations
    const revenueData = [
        { name: 'Jan', value: 85000 },
        { name: 'Feb', value: 92000 },
        { name: 'Mar', value: 78000 },
        { name: 'Apr', value: 95000 },
        { name: 'May', value: 105000 },
        { name: 'Jun', value: 124500 }
    ];

    const ordersData = [
        { name: 'Mon', orders: 42 },
        { name: 'Tue', orders: 38 },
        { name: 'Wed', orders: 51 },
        { name: 'Thu', orders: 47 },
        { name: 'Fri', orders: 56 },
        { name: 'Sat', orders: 63 },
        { name: 'Sun', orders: 59 }
    ];

    const categoryData = [
        { name: 'Electronics', value: 35, fill: '#3b82f6' },
        { name: 'Clothing', value: 25, fill: '#8b5cf6' },
        { name: 'Home', value: 20, fill: '#10b981' },
        { name: 'Sports', value: 15, fill: '#f59e0b' },
        { name: 'Other', value: 5, fill: '#6b7280' }
    ];

    const sparklineData = {
        revenue: [45, 52, 48, 65, 78, 85, 92, 95, 105, 115, 124],
        orders: [180, 190, 185, 200, 210, 225, 235, 247, 245, 250, 256],
        conversion: [3.2, 3.3, 3.1, 3.4, 3.3, 3.2, 3.25, 3.24, 3.3, 3.35, 3.4],
        customers: [1200, 1250, 1280, 1300, 1350, 1400, 1420, 1450, 1480, 1500, 1520]
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-3 border-blue-600 border-t-transparent" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Clean Header */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
                <div className="px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <img src={logo} alt="SureCost" className="h-8 w-auto" />
                        <div className="h-6 w-px bg-gray-300" />
                        <h1 className="text-sm font-semibold text-gray-900">Dashboard</h1>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-3">
                            <div className="h-8 w-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-sm">
                            </div>
                            <span className="text-sm font-medium text-gray-700 hidden sm:inline">{user?.name?.split(' ')[0]}</span>
                        </div>
                        
                        <div className="flex items-center gap-3">
                            {user?.tenant?.isAdmin && (
                                <button 
                                    onClick={() => navigate('/admin')}
                                    style={{ 
                                        all: 'initial',
                                        fontFamily: 'inherit',
                                        background: 'transparent',
                                        border: '0',
                                        padding: '0',
                                        margin: '0',
                                        cursor: 'pointer',
                                        outline: '0',
                                        fontSize: '14px',
                                        lineHeight: '1.5',
                                        color: '#2563eb',
                                        textDecoration: 'none',
                                        WebkitAppearance: 'none',
                                        MozAppearance: 'none',
                                        appearance: 'none',
                                        boxSizing: 'border-box',
                                        display: 'inline-block',
                                        verticalAlign: 'baseline'
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = '#1d4ed8'}
                                    onMouseLeave={(e) => e.target.style.color = '#2563eb'}
                                >
                                    Admin
                                </button>
                            )}
                            
                            <button 
                                onClick={handleLogout}
                                style={{ 
                                    all: 'initial',
                                    fontFamily: 'inherit',
                                    background: 'transparent',
                                    border: '0',
                                    padding: '0',
                                    margin: '0',
                                    cursor: 'pointer',
                                    outline: '0',
                                    fontSize: '14px',
                                    lineHeight: '1.5',
                                    color: '#4b5563',
                                    textDecoration: 'none',
                                    WebkitAppearance: 'none',
                                    MozAppearance: 'none',
                                    appearance: 'none',
                                    boxSizing: 'border-box',
                                    display: 'inline-block',
                                    verticalAlign: 'baseline'
                                }}
                                onMouseEnter={(e) => e.target.style.color = '#1f2937'}
                                onMouseLeave={(e) => e.target.style.color = '#4b5563'}
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <div className="flex">
                {/* Modern Minimalist Filter Sidebar */}
                <aside className="w-64 bg-white border-r border-gray-200 min-h-screen">
                    <div className="p-4">
                        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Filters</h2>
                        
                        {/* Month Filter */}
                        <div className="mb-6">
                            <label className="text-sm font-medium text-gray-700 mb-2 block">Month</label>
                            <div style={{ position: 'relative', display: 'block', width: '100%' }}>
                                <select 
                                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white hover:border-gray-300 focus:outline-none focus:border-blue-500 transition-colors cursor-pointer"
                                    style={{ 
                                        appearance: 'none',
                                        WebkitAppearance: 'none',
                                        MozAppearance: 'none',
                                        paddingRight: '2.5rem',
                                        backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%236b7280\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpath d=\'M19 9l-7 7-7-7\'/%3e%3c/svg%3e")',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'right 0.75rem center',
                                        backgroundSize: '1rem 1rem'
                                    }}
                                    value={filters.month}
                                    onChange={(e) => updateFilter('month', e.target.value)}
                                >
                                    {months.map((month) => (
                                        <option key={month.key} value={month.key}>
                                            {month.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Date Range */}
                        <div className="mb-6">
                            <label className="text-sm font-medium text-gray-700 mb-2 block">Date Range</label>
                            <div style={{ position: 'relative', display: 'block', width: '100%' }}>
                                <select 
                                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white hover:border-gray-300 focus:outline-none focus:border-blue-500 transition-colors cursor-pointer"
                                    style={{ 
                                        appearance: 'none',
                                        WebkitAppearance: 'none',
                                        MozAppearance: 'none',
                                        paddingRight: '2.5rem',
                                        backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%236b7280\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpath d=\'M19 9l-7 7-7-7\'/%3e%3c/svg%3e")',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'right 0.75rem center',
                                        backgroundSize: '1rem 1rem'
                                    }}
                                    value={filters.dateRange}
                                    onChange={(e) => updateFilter('dateRange', e.target.value)}
                                >
                                    <option value="1m">1 month</option>
                                    <option value="3m">3 months</option>
                                    <option value="6m">6 months</option>
                                    <option value="12m">12 months</option>
                                </select>
                            </div>
                        </div>

                        {/* Cohort Filter */}
                        <div className="mb-6">
                            <label className="text-sm font-medium text-gray-700 mb-2 block">Cohort Filter</label>
                            <div style={{ position: 'relative', display: 'block', width: '100%' }}>
                                <select 
                                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white hover:border-gray-300 focus:outline-none focus:border-blue-500 transition-colors cursor-pointer"
                                    style={{ 
                                        appearance: 'none',
                                        WebkitAppearance: 'none',
                                        MozAppearance: 'none',
                                        paddingRight: '2.5rem',
                                        backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%236b7280\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpath d=\'M19 9l-7 7-7-7\'/%3e%3c/svg%3e")',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'right 0.75rem center',
                                        backgroundSize: '1rem 1rem'
                                    }}
                                    value={filters.cohort}
                                    onChange={(e) => updateFilter('cohort', e.target.value)}
                                >
                                    <option value="all">All Cohorts</option>
                                    {cohorts.map((cohort) => (
                                        <option key={cohort.id} value={cohort.id}>
                                            {cohort.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* GDC */}
                        <div className="mb-6">
                            <label className="text-sm font-medium text-gray-700 mb-2 block">GDC</label>
                            <GDCSearchableDropdown 
                                gdcs={gdcs}
                                value={filters.gdc}
                                onChange={(value) => updateFilter('gdc', value)}
                            />
                        </div>

                        {/* Vendor Filter - Only for Admin Tenants */}
                        {user && user.tenant && user.tenant.isAdmin && (
                            <div className="mb-6">
                                <label className="text-sm font-medium text-gray-700 mb-2 block">Vendor (Admin only)</label>
                                <VendorSearchableDropdown 
                                    vendors={vendors}
                                    value={filters.vendor}
                                    onChange={(value) => updateFilter('vendor', value)}
                                />
                            </div>
                        )}

                        {/* Clear Filters */}
                        <button 
                            onClick={() => setFilters({
                                dateRange: '1m',
                                month: '202505', // Reset to May 2025 where we have data
                                cohort: 'all',
                                gdc: 'all',
                                vendor: 'all'
                            })}
                            className="outline-none border-none bg-transparent p-0 m-0"
                            style={{ 
                                all: 'unset', 
                                cursor: 'pointer', 
                                width: '100%', 
                                padding: '8px 12px', 
                                fontSize: '14px', 
                                fontWeight: '500', 
                                color: '#4b5563', 
                                backgroundColor: '#f9fafb', 
                                borderRadius: '8px', 
                                textAlign: 'center', 
                                display: 'block',
                                boxSizing: 'border-box'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#f9fafb'}
                        >
                            Clear All Filters
                        </button>
                    </div>

                </aside>

                {/* Main Dashboard Content */}
                <div className="flex-1 p-6">
                    {/* Dynamic Vendor Header */}
                    <div className="mb-8">
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <div className="flex items-center justify-between">
                                <h1 className="text-2xl font-bold text-gray-900">
                                    {(() => {
                                        if (filters.vendor === 'all') {
                                            return 'All Vendors Pricing Dashboard';
                                        }
                                        const selectedVendor = vendors.find(v => v.id === filters.vendor);
                                        const vendorName = selectedVendor ? selectedVendor.name : filters.vendor;
                                        return `${vendorName} Pricing Dashboard`;
                                    })()}
                                </h1>
                                
                                {/* Export Dropdown */}
                                <div style={{ position: 'relative' }} data-export-menu>
                                <button
                                    onClick={() => !isExporting && setShowExportMenu(!showExportMenu)}
                                    disabled={isExporting}
                                    style={{
                                        all: 'initial',
                                        fontFamily: 'inherit',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '8px 16px',
                                        backgroundColor: isExporting ? '#f3f4f6' : '#f9fafb',
                                        color: isExporting ? '#9ca3af' : '#374151',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        borderRadius: '8px',
                                        border: '1px solid #e5e7eb',
                                        cursor: isExporting ? 'not-allowed' : 'pointer',
                                        outline: 'none',
                                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                        transition: 'background-color 0.2s ease',
                                        WebkitAppearance: 'none',
                                        MozAppearance: 'none',
                                        appearance: 'none',
                                        boxSizing: 'border-box',
                                        verticalAlign: 'baseline',
                                        opacity: isExporting ? 0.7 : 1
                                    }}
                                    onMouseEnter={(e) => !isExporting && (e.target.style.backgroundColor = '#f3f4f6')}
                                    onMouseLeave={(e) => !isExporting && (e.target.style.backgroundColor = '#f9fafb')}
                                >
                                    {/* Loading Spinner or Download Icon */}
                                    {isExporting ? (
                                        <svg 
                                            style={{
                                                width: '16px',
                                                height: '16px',
                                                fill: 'none',
                                                stroke: 'currentColor',
                                                animation: 'spin 1s linear infinite'
                                            }}
                                            viewBox="0 0 24 24"
                                        >
                                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" opacity="0.25"/>
                                            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round"/>
                                        </svg>
                                    ) : (
                                        <svg 
                                            style={{
                                                width: '16px',
                                                height: '16px',
                                                fill: 'none',
                                                stroke: 'currentColor'
                                            }}
                                            viewBox="0 0 24 24"
                                        >
                                            <path 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round" 
                                                strokeWidth={2} 
                                                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                                            />
                                        </svg>
                                    )}
                                    
                                    {isExporting ? (
                                        `Generating ${exportingFormat?.toUpperCase()}...`
                                    ) : (
                                        'Export'
                                    )}
                                    
                                    {/* Dropdown Arrow - hidden when exporting */}
                                    {!isExporting && (
                                        <svg 
                                            style={{
                                                width: '12px',
                                                height: '12px',
                                                marginLeft: '4px',
                                                fill: 'none',
                                                stroke: 'currentColor',
                                                transform: showExportMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s ease'
                                            }}
                                            viewBox="0 0 24 24"
                                        >
                                            <path 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round" 
                                                strokeWidth={2} 
                                                d="M19 9l-7 7-7-7" 
                                            />
                                        </svg>
                                    )}
                                </button>
                                
                                {/* Export Dropdown Menu */}
                                {showExportMenu && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        right: '0',
                                        marginTop: '4px',
                                        backgroundColor: 'white',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                        border: '1px solid #e5e7eb',
                                        zIndex: 50,
                                        minWidth: '180px'
                                    }}>
                                        <button
                                            onClick={() => handleExport('pdf')}
                                            style={{
                                                all: 'initial',
                                                fontFamily: 'inherit',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                width: '100%',
                                                padding: '10px 12px',
                                                backgroundColor: 'transparent',
                                                color: '#374151',
                                                fontSize: '14px',
                                                fontWeight: '400',
                                                cursor: 'pointer',
                                                border: 'none',
                                                outline: 'none',
                                                textAlign: 'left',
                                                transition: 'background-color 0.2s ease',
                                                boxSizing: 'border-box'
                                            }}
                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                        >
                                            <svg 
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    fill: 'none',
                                                    stroke: '#dc2626'
                                                }}
                                                viewBox="0 0 24 24"
                                            >
                                                <path 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round" 
                                                    strokeWidth={2} 
                                                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" 
                                                />
                                            </svg>
                                            Export as PDF
                                        </button>
                                        
                                        <button
                                            onClick={() => handleExport('xls')}
                                            style={{
                                                all: 'initial',
                                                fontFamily: 'inherit',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                width: '100%',
                                                padding: '10px 12px',
                                                backgroundColor: 'transparent',
                                                color: '#374151',
                                                fontSize: '14px',
                                                fontWeight: '400',
                                                cursor: 'pointer',
                                                border: 'none',
                                                outline: 'none',
                                                textAlign: 'left',
                                                transition: 'background-color 0.2s ease',
                                                boxSizing: 'border-box',
                                                borderTop: '1px solid #e5e7eb'
                                            }}
                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                        >
                                            <svg 
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    fill: 'none',
                                                    stroke: '#059669'
                                                }}
                                                viewBox="0 0 24 24"
                                            >
                                                <path 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round" 
                                                    strokeWidth={2} 
                                                    d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" 
                                                />
                                            </svg>
                                            Export as Excel
                                        </button>
                                    </div>
                                )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Compact KPI Cards */}
                    <div className="grid grid-cols-4 gap-4 mb-6">
                        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                            <div className="text-center">
                                <p className="text-xs font-medium text-gray-500 mb-1">Above market</p>
                                <p className="text-2xl font-bold text-gray-900 mb-1">
                                    {kpiData.aboveMarket.toLocaleString()}
                                </p>
                                <p className="text-xs text-green-600 font-medium">{kpiData.aboveMarketPct}%</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                            <div className="text-center">
                                <p className="text-xs font-medium text-gray-500 mb-1">Above leader</p>
                                <p className="text-2xl font-bold text-gray-900 mb-1">
                                    {kpiData.aboveLeader.toLocaleString()}
                                </p>
                                <p className="text-xs text-green-600 font-medium">{kpiData.aboveLeaderPct}%</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                            <div className="text-center">
                                <p className="text-xs font-medium text-gray-500 mb-1">At/below market</p>
                                <p className="text-2xl font-bold text-gray-900 mb-1">
                                    {kpiData.atOrBelowMarket.toLocaleString()}
                                </p>
                                <p className="text-xs text-green-600 font-medium">{kpiData.atOrBelowMarketPct}%</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                            <div className="text-center">
                                <p className="text-xs font-medium text-gray-500 mb-1">At/below leader</p>
                                <p className="text-2xl font-bold text-gray-900 mb-1">
                                    {kpiData.atOrBelowLeader.toLocaleString()}
                                </p>
                                <p className="text-xs text-gray-500 font-medium">Count only</p>
                            </div>
                        </div>
                    </div>

                    {/* Band Distribution Chart */}
                    <div className="mb-4">
                        <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                            <div className="mb-2 flex items-center">
                                <span className="text-sm font-semibold text-gray-900">
                                    Band Distribution (vs. Market)
                                </span>
                                <Tooltip content="Band is a price range compared to the market. This chart shows how many of your items fall into each range: lower than market (green), about the same (gray), or higher (red).">
                                    <svg 
                                        className="ml-2 w-4 h-4 text-gray-400 cursor-help inline-block" 
                                        fill="currentColor" 
                                        viewBox="0 0 20 20"
                                        style={{ verticalAlign: 'baseline', transform: 'translateY(-1px)' }}
                                    >
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                    </svg>
                                </Tooltip>
                            </div>
                            <div className="flex items-center gap-1">
                                {bandDistribution.length > 0 ? bandDistribution.map((item, index) => (
                                    <div key={index} className="flex-1 text-center">
                                        <div 
                                            className="rounded-md p-2 border transition-all hover:shadow-sm"
                                            style={{ 
                                                backgroundColor: item.bgColor,
                                                borderColor: item.color + '40'
                                            }}
                                        >
                                            <div 
                                                className="text-xl font-bold mb-0.5"
                                                style={{ color: item.color }}
                                            >
                                                {item.value}
                                            </div>
                                            <div className="text-xs text-gray-600 font-medium leading-tight">
                                                {item.label}
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    // Show loading placeholder
                                    <div className="flex items-center gap-1">
                                        {[1,2,3,4,5,6].map((i) => (
                                            <div key={i} className="flex-1 text-center">
                                                <div className="rounded-md p-2 border bg-gray-50 animate-pulse">
                                                    <div className="h-6 bg-gray-200 rounded mb-1"></div>
                                                    <div className="h-3 bg-gray-200 rounded"></div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        
                    </div>

                    {/* Pricing Intelligence Table */}
                    <div className="mb-6">
                        <PricingIntelligenceTable filters={filters} />
                    </div>

                    {/* Charts Row */}
                    <div className="grid grid-cols-3 gap-4 mb-6" style={{ display: 'none' }}>
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <h3 className="text-sm font-semibold text-gray-900 mb-4">Revenue Trend</h3>
                            <ResponsiveContainer width="100%" height={200}>
                                <AreaChart data={revenueData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                                    <defs>
                                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1}/>
                                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                                    <YAxis hide />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: '#fff', 
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            fontSize: '12px' 
                                        }}
                                        formatter={(value) => `$${(value/1000).toFixed(0)}K`}
                                    />
                                    <Area 
                                        type="monotone" 
                                        dataKey="value" 
                                        stroke="#3b82f6" 
                                        fillOpacity={1} 
                                        fill="url(#colorRevenue)" 
                                        strokeWidth={2} 
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <h3 className="text-sm font-semibold text-gray-900 mb-4">Weekly Performance</h3>
                            <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={ordersData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                                    <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                                    <YAxis hide />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: '#fff', 
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            fontSize: '12px' 
                                        }}
                                    />
                                    <Bar dataKey="orders" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <h3 className="text-sm font-semibold text-gray-900 mb-4">Category Distribution</h3>
                            <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                    <Pie
                                        data={categoryData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={80}
                                        paddingAngle={2}
                                        dataKey="value"
                                    >
                                        {categoryData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                        ))}
                                    </Pie>
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: '#fff', 
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            fontSize: '12px' 
                                        }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                </div>
            </div>
            
        </div>
    );
};

export default Dashboard;
